import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { HorizontalToolbarViewportProps } from "../../../types/imageStudio/toolbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";

export const HorizontalToolbarViewport: React.FC<HorizontalToolbarViewportProps> = ({
    children,
    className = ""
}) => {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const viewportRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const rafRef = useRef<number | null>(null);

    const [hasOverflow, setHasOverflow] = useState(false);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollState = () => {
        if (rafRef.current != null) {
            cancelAnimationFrame(rafRef.current);
        }

        rafRef.current = requestAnimationFrame(() => {
            const root = rootRef.current;
            const viewport = viewportRef.current;
            const content = contentRef.current;

            if (!root || !viewport || !content) return;

            const tolerance = 2;
            const parentWidth = root.parentElement?.clientWidth ?? root.clientWidth;
            const contentWidth = content.scrollWidth;

            const nextHasOverflow = contentWidth > parentWidth + tolerance;

            if (!nextHasOverflow) {
                if (viewport.scrollLeft !== 0) {
                    viewport.scrollLeft = 0;
                }

                setHasOverflow(false);
                setCanScrollLeft(false);
                setCanScrollRight(false);
                return;
            }

            const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);

            if (viewport.scrollLeft > maxScrollLeft) {
                viewport.scrollLeft = maxScrollLeft;
            }

            const currentScrollLeft = Math.max(0, viewport.scrollLeft);

            setHasOverflow(true);
            setCanScrollLeft(currentScrollLeft > tolerance);
            setCanScrollRight(currentScrollLeft < maxScrollLeft - tolerance);
        });
    };

    const getToolbarSections = () => {
        const content = contentRef.current;
        if (!content) return [];

        const row = content.firstElementChild as HTMLElement | null;
        if (!row) return [];

        return Array.from(row.children) as HTMLElement[];
    };

    const scrollToSection = (direction: "left" | "right") => {
        const viewport = viewportRef.current;
        if (!viewport) return;

        const sections = getToolbarSections();
        if (sections.length < 1) return;

        const currentLeft = viewport.scrollLeft;
        const viewportRight = currentLeft + viewport.clientWidth;

        let nextLeft = currentLeft;

        if (direction === "right") {
            const nextSection = sections.find(section =>
                section.offsetLeft + section.offsetWidth > viewportRight + 2
            );

            nextLeft = nextSection
                ? nextSection.offsetLeft
                : viewport.scrollWidth - viewport.clientWidth;
        }

        if (direction === "left") {
            const previousSections = sections.filter(section =>
                section.offsetLeft < currentLeft - 2
            );

            const previousSection = previousSections[previousSections.length - 1];

            nextLeft = previousSection ? previousSection.offsetLeft : 0;
        }

        viewport.scrollTo({
            left: Math.max(0, nextLeft),
            behavior: "smooth"
        });
    };

    useLayoutEffect(() => {
        updateScrollState();
    }, [children]);

    useEffect(() => {
        const root = rootRef.current;
        const viewport = viewportRef.current;
        const content = contentRef.current;

        if (!root || !viewport || !content) return;

        const resizeObserver = new ResizeObserver(updateScrollState);

        resizeObserver.observe(root);
        resizeObserver.observe(viewport);
        resizeObserver.observe(content);

        if (root.parentElement) {
            resizeObserver.observe(root.parentElement);
        }

        viewport.addEventListener("scroll", updateScrollState, { passive: true });
        window.addEventListener("resize", updateScrollState);

        updateScrollState();

        return () => {
            if (rafRef.current != null) {
                cancelAnimationFrame(rafRef.current);
            }

            resizeObserver.disconnect();
            viewport.removeEventListener("scroll", updateScrollState);
            window.removeEventListener("resize", updateScrollState);
        };
    }, []);

    return (
        <div
            ref={rootRef}
            className={`toolbar-horizontal-root ${hasOverflow ? "toolbar-horizontal-root-overflow" : ""} ${className}`}
        >
            {canScrollLeft && (
                <button
                    type="button"
                    className="toolbar-horizontal-arrow btn btn-sm btn-outline-secondary"
                    onClick={() => scrollToSection("left")}
                >
                    <FontAwesomeIcon icon={faChevronLeft} size="sm" />
                </button>
            )}

            <div
                ref={viewportRef}
                className="toolbar-horizontal-scroll"
            >
                <div
                    ref={contentRef}
                    className="toolbar-horizontal-content"
                >
                    {children}
                </div>
            </div>

            {canScrollRight && (
                <button
                    type="button"
                    className="toolbar-horizontal-arrow btn btn-sm btn-outline-secondary"
                    onClick={() => scrollToSection("right")}
                >
                    <FontAwesomeIcon icon={faChevronRight} size="sm" />
                </button>
            )}
        </div>
    );
};